/**
 * BillboardComponent renders a flat sprite that always faces the camera.
 * Wraps a THREE.Sprite with options for depth testing, transparency, and texture
 * loading. Useful for lightweight effects like markers or simplified objects.
 */
import * as THREE from 'three';

import { AssetPath } from '../../storage/storageProvider.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { getLatest } from '../../utils/Async.js';
import { IAsyncLoadableTag } from '../../utils/IAsyncLoadable.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { IAsyncLoadable } from '../../utils/IAsyncLoadable.js';
import type { TexturePath } from '../../utils/serialization/decorator.js';
import type { SceneComponentOptions } from '../SceneComponent.js';

export interface BillboardComponentOptions extends SceneComponentOptions {
  imageUrl?: string;
  depthTest?: boolean;
  depthWrite?: boolean;
  transparent?: boolean;
  toneMapped?: boolean;
  //TODO: Allow a custom material to be passed in
}


@EngineClass('BillboardComponent')
export class BillboardComponent extends SceneComponent implements IAsyncLoadable {
  readonly [IAsyncLoadableTag] = true;



  @property({ required: true, description: 'Image URL' })
  public imageUrl: TexturePath = '';

  @property()
  public depthTest: boolean = true;

  @property()
  public depthWrite: boolean = true;

  @property()
  public transparent: boolean = true;

  @property()
  protected toneMapped: boolean = true;

  private sprite: THREE.Sprite | null = null;
  private spriteMaterial: THREE.SpriteMaterial | null = null;

  private loadPromise: Promise<unknown> | null = null;


  public override initialize(options?: BillboardComponentOptions): void {
    super.initialize(options);
    this.imageUrl = options?.imageUrl ?? this.imageUrl;
    this.depthTest = options?.depthTest ?? this.depthTest;
    this.depthWrite = options?.depthWrite ?? this.depthWrite;
    this.transparent = options?.transparent ?? this.transparent;
    this.toneMapped = options?.toneMapped ?? this.toneMapped;
    this.createSprite();
  }

  public override postLoad(): void {
    super.postLoad();
    this.createSprite();
  }

  public createSprite(): void {
    this.spriteMaterial = new THREE.SpriteMaterial({
      map: null,
      depthTest: this.depthTest,
      depthWrite: this.depthWrite,
      transparent: this.transparent,
      toneMapped: this.toneMapped,
    });
    this.sprite = new THREE.Sprite(this.spriteMaterial);
    this.add(this.sprite);
  }

  public override beginPlay(): void {
    super.beginPlay();

    // Load initial texture if provided
    if (this.imageUrl.trim() !== '') {
      this.loadSprite(this.imageUrl);
    }

    // Apply initial postprocessing exclusion state
    this.updateToneMappingExclusion();
  }

  // start IAsyncLoadable implementation
  public async waitForLoad(): Promise<void> {
    if (this.loadPromise) await this.loadPromise;
  }

  public isLoading(): boolean {
    return this.loadPromise !== null;
  }
  // end IAsyncLoadable implementation

  public loadSprite(url: string): void {
    this.imageUrl = url;

    // Handle empty URL case - clear the texture
    if (url.trim() === '') {
      if (this.sprite) {
        if (this.sprite.material.map) {
          this.sprite.material.map.dispose();
        }
        this.sprite.material.map = null;
        this.sprite.material.needsUpdate = true;
      }
      return;
    }

    // Use getLatest to prevent race conditions when imageUrl changes rapidly
    const promise = getLatest(
      this,
      'texture',
      () => resourceManager.loadTexture(AssetPath.fromString(url))
    );
    this.loadPromise = promise;
    promise.then((texture) => {
      if (texture !== undefined && this.sprite) {
        if (this.sprite.material.map) {
          this.sprite.material.map.dispose();
        }
        this.sprite.material.map = texture;
        this.sprite.material.needsUpdate = true;
      }
    }).finally(() => {
      if (this.loadPromise === promise) {
        this.loadPromise = null;
      }
    });
  }

  public getToneMapped(): boolean {
    return this.toneMapped;
  }
  public setToneMapped(toneMapped: boolean) {
    if (this.toneMapped === toneMapped) return; // No change

    this.toneMapped = toneMapped;
    if (this.sprite) {
      this.sprite.material.toneMapped = toneMapped;
    }
    this.updateToneMappingExclusion();
  }

  private updateToneMappingExclusion(): void {
    const world = this.getWorld();
    if (world?.postProcessManager) {
      if (this.toneMapped) {
        world.postProcessManager.removeExcludedObjects(this);
      } else {
        world.postProcessManager.addExcludedObjects(this);
      }
    }
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);
    if (path === 'imageUrl') {
      this.loadSprite(value);
    } else if (path === 'toneMapped') {
      this.setToneMapped(value);
    }
  }

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    if (this.imageUrl.trim() !== '') {
      this.loadSprite(this.imageUrl);
    }
    this.updateToneMappingExclusion();
  }

  public override onEditorRemoveFromWorld(): void {
    super.onEditorRemoveFromWorld();
    if (!this.toneMapped) {
      this.getWorld()?.postProcessManager?.removeExcludedObjects(this);
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Billboard';
  }
}